import type { SchemaReploShopifyProduct } from "schemas/generated/product";
import type { ProductRef } from "schemas/product";

import React from "react";

import { ProductSelector } from "@v1/components/common/designSystem/ProductSelector";
import { useFetchStoreProducts } from "@v1/hooks/useStoreProducts";
import { mapStoreProductToReploProduct } from "replo-runtime/shared/mappers/product";

export const BuildAssistantSelectProduct: React.FC<{
  selectedProduct?: SchemaReploShopifyProduct | null;
  onProductChange: (product: SchemaReploShopifyProduct | null) => void;
}> = ({ onProductChange, selectedProduct }) => {
  const selectedProductRefProp = selectedProduct
    ? {
        productId: selectedProduct.id,
        title: selectedProduct.title,
        id: selectedProduct.id,
      }
    : null;
  const [selectedProductRef, setSelectedProductRef] =
    React.useState<ProductRef | null>(selectedProductRefProp);

  const currentProductRef = React.useRef<ProductRef | null>(
    selectedProductRefProp,
  );

  const { products } = useFetchStoreProducts({
    query: {
      type: "specificExternalIds",
      externalIds: [Number(selectedProductRef?.productId)],
    },
    forceSkip: !selectedProductRef?.productId,
  });

  // Patrick (2025-02-21) - Ideally we don't need this effect.
  // This effect should be handled in the handleProductChange function below with some async await
  // but we need to use the useFetchStoreProducts hook in order to get the right product.
  // Then we need to map it to the replo product. Because we need the hook, we use it in the effect here.
  React.useEffect(() => {
    if (
      products &&
      products.length > 0 &&
      currentProductRef.current !== selectedProductRef
    ) {
      if (products[0]) {
        const product = mapStoreProductToReploProduct(products[0], {
          currencyCode: "USD",
          language: "en",
          // biome-ignore lint/suspicious/noTemplateCurlyInString: test
          moneyFormat: "${{amount}}",
          // biome-ignore lint/suspicious/noTemplateCurlyInString: test
          moneyWithCurrencyFormat: "${{amount}} USD",
          metafields: {
            productMetafieldsMapping: {},
            variantMetafieldsMapping: {},
          },
          quantity: 1,
        });

        if (product) {
          onProductChange(product);
        }
      }

      currentProductRef.current = selectedProductRef;
    } else if (currentProductRef.current !== selectedProductRef) {
      onProductChange(null);
    }
  }, [products, selectedProductRef, onProductChange]);

  const handleProductChange = (newProduct: ProductRef | null) => {
    setSelectedProductRef(newProduct);
  };

  return (
    <ProductSelector
      selectedProductRef={selectedProductRef}
      onChange={handleProductChange}
      isMultiProducts={false}
      allowDynamicData={false}
      size="sm"
      onRemove={() => {
        setSelectedProductRef(null);
      }}
      triggerLayoutClassName="w-fit max-w-[250px]"
      placeholderText="Product"
    />
  );
};
